import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toggleApplicationView } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { CampaignInvite } from 'types/campaign/invites';
import { formatNumber } from 'utils/helpers';

interface InvitesTableProps {
    invites: CampaignInvite[];
    hasMore: boolean;
    loading: boolean;
    initialLoading: boolean;
    error: string | null;
    onNextPage: () => void;
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const PlatformIcon = ({ platform }: { platform: string }) => {
    if (platform === 'ig') {
        return <i className="ri-instagram-line text-danger me-1" aria-hidden />;
    }
    if (platform === 'yt') {
        return <i className="ri-youtube-fill text-danger me-1" aria-hidden />;
    }
    return null;
};

const InvitesTable: React.FC<InvitesTableProps> = ({
    invites,
    hasMore,
    loading,
    initialLoading,
    error,
    onNextPage,
}) => {
    const dispatch: AppDispatch = useDispatch();
    const { campaignId } = useParams();

    const openSocialAccountView = (socialAccountId?: string) => {
        if (!socialAccountId) return;
        dispatch(
            toggleApplicationView({
                socialAccountId,
                campaignId,
                showPermalink: false,
            })
        );
    };

    return (
        <InfiniteScroll
            dataLength={invites.length}
            next={onNextPage}
            hasMore={hasMore}
            loader={<></>}
            className="w-100"
        >
            <table>
                <thead>
                    <tr>
                        <th>Influencer</th>
                        <th>Handle</th>
                        <th>Followers</th>
                        <th>Engagement Rate</th>
                        <th>Applied</th>
                        <th>Collaborated</th>
                    </tr>
                </thead>
                <tbody>
                    {!initialLoading &&
                        invites.map((invite, i) => {
                            const handle = invite.social_account?.handle;
                            const platform = invite.social_account?.platform;
                            const influencerName =
                                invite.influencer?.name || invite.social_account?.name || '';
                            const socialAccountId = invite.social_account?.uuid;

                            return (
                                <tr
                                    className="border-bottom-1 px-3"
                                    key={`${socialAccountId}-${invite.created}-${i}`}
                                >
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-start align-items-center gap-2 border-right-dashed td-content">
                                                <span className="influencer-name">
                                                    {influencerName || '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                {handle ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link p-0 text-decoration-none text-primary d-inline-flex align-items-center"
                                                        onClick={() => openSocialAccountView(socialAccountId)}
                                                    >
                                                        <PlatformIcon platform={platform} />
                                                        {handle}
                                                    </button>
                                                ) : (
                                                    '-'
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                <span>
                                                    {formatNumber(
                                                        invite.social_account?.followers_count ?? 0
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                <span>
                                                    {invite.social_account?.engagement_score ?? '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                <span>{yesNo(invite.applied)}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="py-3">
                                            <div className="d-flex justify-content-center align-items-center gap-1 td-content">
                                                <span>{yesNo(invite.collaborated)}</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    {(loading || initialLoading) &&
                        Array.from({ length: 10 }).map((_, i) => (
                            <tr key={`skeleton-${i}`}>
                                {Array.from({ length: 6 }).map((__, j) => (
                                    <td key={j} height={30}>
                                        <Skeleton />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            </table>
            {!(loading || initialLoading) && error && (
                <div className="py-3 d-flex justify-content-center align-items-center">
                    <button
                        type="button"
                        className="theme-btn theme-btn-sm btn-primary-outlined"
                        onClick={onNextPage}
                    >
                        Load More <i className="ms-2 fs-5 bx bx-refresh" />
                    </button>
                </div>
            )}
            {!(loading || initialLoading) && invites.length === 0 && (
                <div className="d-flex w-100 justify-content-center align-items-center my-5">
                    <div className="length-zero my-5">
                        <h3>Currently there are no invites in your list</h3>
                    </div>
                </div>
            )}
        </InfiniteScroll>
    );
};

export default InvitesTable;
